/**
 * AccesosPage — `/activa/accesos`: quién puede entrar a DEENEX Activa.
 *
 * Se entra con el mail: a cada persona de esta lista le llega un código cada vez que entra, no hay
 * contraseñas. Sólo los dueños (ACTIVA_DUENOS, en el backend) suman, editan, quitan y devuelven
 * accesos; a un dueño no se le quita desde acá (primero se lo saca de la variable y se redespliega, y
 * recién ahí se le quita como a cualquiera). El resto del equipo ve la lista y quiénes son los dueños.
 *
 * Todo pasa en la página: «Quitar acceso» y «Desbloquear el ingreso» se confirman en la misma fila (sin window.confirm ni modal) y
 * el resultado de un alta queda escrito (role=status), no en un toast que se va. Un error del backend
 * se muestra tal cual, y el formulario se limpia sólo si salió bien.
 *
 * Cada persona es del EQUIPO de Deenex (entra a todos los comercios y hace todo) o de un CLIENTE (entra
 * sólo a los comercios que se le eligen: ve lo suyo, contesta conversaciones y maneja las bajas). Se
 * elige al darle el acceso, sin nada marcado de entrada: equivocarse para el lado del equipo es darle a
 * un cliente los datos de todos los demás. Un dueño es siempre del equipo. Una persona de un cliente no
 * ve esta página (el backend le responde 403 SOLO_EQUIPO).
 *
 * Datos REALES del backend (`/api/v1/accesos`, con el token de persona del login por mail).
 */

import { useCallback, useEffect, useState } from "react";
import { Loader2, LockOpen, Pencil, Undo2, UserMinus, UserPlus } from "lucide-react";
import { accesosApi, type ErrorAccesos, type ListaAccesos, type PersonaAcceso, type RolAcceso } from "./api";
import SelectorComercios, { MAX_COMERCIOS, nombresDeComercios, opcionesDeComercios, type OpcionComercio } from "./SelectorComercios";
import { avisarSesionVencida } from "../../services/sesion-events";
import { AvisoAmbar, CajaError } from "../wa-oficial/components/base/comun";
import { EncabezadoPagina, Pagina } from "../../components/activa/Pagina";

/**
 * Desde el 06/10/2026 se acepta cualquier mail (decisión de Alan); alguien de afuera sumado al EQUIPO
 * entra a todos los comercios, y la página lo avisa antes de dar el acceso (a un cliente no: entra sólo
 * a los suyos). El backend puede volver a exigir @deenex.tech al equipo con ACCESOS_SOLO_DEENEX=true:
 * ahí responde EQUIPO_FUERA_DE_DOMINIO y se muestra su texto.
 */
const DOMINIO_EQUIPO = "@deenex.tech";
const AVISO_MAIL_DE_AFUERA = "Es un mail de afuera de Deenex: como equipo va a poder entrar y operar todos los comercios. Si es de un cliente, elegí «Cliente».";
const FALTA_ROL = "Elegí si es del equipo de Deenex o de un cliente.";
const FALTAN_COMERCIOS = `Elegí entre 1 y ${MAX_COMERCIOS} comercios.`;

/** Qué puede hacer cada rol, dicho para quien da el acceso. */
const AYUDA_ROL: Record<RolAcceso, string> = {
  equipo: "Alguien del equipo de Deenex entra a todos los comercios y puede operar cualquiera.",
  comercio: "Alguien de un cliente entra sólo a los comercios que elijas: ve lo suyo, contesta conversaciones y maneja las bajas. Armar y lanzar campañas, las plantillas, la base y la plata quedan para el equipo.",
};
const AYUDA_DESBLOQUEO = "Si alguien no puede entrar desde un navegador nuevo, o se equivocó muchas veces con el código, usá «Desbloquear el ingreso» en su fila.";

/** El rol de una fila: un backend de antes de los roles no lo manda, y ahí todos eran del equipo. */
const rolDe = (p: PersonaAcceso): RolAcceso => (p.rol === "comercio" ? "comercio" : "equipo");

/**
 * «Equipo de Deenex · todos los comercios» o «Cliente · HATSU · hatsu-palermo, Sushi Pop». Cada comercio con su
 * nombre (`nombresDeComercios`); el identificador sólo si no se sabe el nombre: antes era siempre el identificador.
 */
function aQueEntra(p: PersonaAcceso, nombres: Map<string, string>): string {
  if (rolDe(p) !== "comercio") return "Equipo de Deenex · todos los comercios";
  return `Cliente · ${(p.comercios || []).map((c) => nombres.get(c) || c).join(", ") || "sin comercios"}`;
}

/** Los que el backend aceptó sin poder confirmar (la plataforma no respondió): se avisa para revisarlos. */
function avisoSinVerificar(lista: string[] | undefined): string {
  if (!lista?.length) return "";
  const cuales = lista.map((c) => `«${c}»`).join(", ");
  return ` Ojo: no pudimos confirmar que ${lista.length === 1 ? `exista ${cuales}` : `existan ${cuales}`} (el catálogo de la plataforma no respondió): revisalo.`;
}

/** Elegir equipo o cliente: sin nada marcado de entrada en el alta. */
function ElegirRol({ valor, onCambio, nombre, sinCliente }: { valor: RolAcceso | ""; onCambio: (r: RolAcceso) => void; nombre: string; sinCliente?: string }) {
  return (
    <fieldset>
      <legend className="text-xs font-semibold text-slate-600">Es</legend>
      <div className="mt-1 flex flex-wrap gap-4">
        <label className="inline-flex items-center gap-1.5 text-sm text-slate-700">
          <input type="radio" name={nombre} value="equipo" checked={valor === "equipo"} onChange={() => onCambio("equipo")} className="text-violet-600 focus:ring-violet-500" />
          Equipo Deenex
        </label>
        <label className={`inline-flex items-center gap-1.5 text-sm ${sinCliente ? "text-slate-400" : "text-slate-700"}`}>
          <input type="radio" name={nombre} value="comercio" checked={valor === "comercio"} onChange={() => onCambio("comercio")} disabled={!!sinCliente} className="text-violet-600 focus:ring-violet-500" />
          Cliente
        </label>
      </div>
      {sinCliente && <p className="text-[11px] text-slate-400 mt-1">{sinCliente}</p>}
    </fieldset>
  );
}
const MAIL_RE = /^\S+@\S+\.\S+$/;
/** Quién sumó a los dueños: la configuración, no una persona. */
const POR_CONFIGURACION = "ACTIVA_DUENOS";

/**
 * A quien le quitaron el acceso se le cierra la sesión, como cuando vence: AuthContext oye el aviso y
 * lleva a /login. Antes la página sólo decía «Ya no tenés acceso.» y el resto de la app seguía abierto.
 */
const esSinAcceso = (e: unknown) => {
  const err = e as ErrorAccesos;
  return err?.status === 403 && err?.code === "SIN_ACCESO";
};

/** Una persona de un cliente que llegó acá (por un link): la lista es del equipo. */
const esSoloEquipo = (e: unknown) => (e as ErrorAccesos)?.code === "SOLO_EQUIPO";

/** Cualquier otro 403 al pedir la lista (una sesión que no es de una persona, por ejemplo): reintentar no lo arregla. */
const esSinPermiso = (e: unknown) => (e as ErrorAccesos)?.status === 403;
const TEXTO_SIN_PERMISO = "No tenés permiso para ver esta lista. La ve el equipo de Deenex, entrando con su mail.";

function fmtFecha(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
}

/** «el 20 sept 2026», o nada si no hay fecha. */
const elDia = (iso?: string | null) => (fmtFecha(iso) ? ` el ${fmtFecha(iso)}` : "");

/** «Alan», «Alan y Tecnología», «Alan, Beto y Tecnología». */
function enumerar(nombres: string[]): string {
  if (nombres.length <= 1) return nombres[0] || "";
  return `${nombres.slice(0, -1).join(", ")} y ${nombres[nombres.length - 1]}`;
}

/** Lo que la página puede decir de un mail antes de mandarlo. El backend vuelve a validar todo. */
function problemaDelMail(email: string): string {
  if (!MAIL_RE.test(email)) return "Ingresá un mail válido.";
  return "";
}

function loSumo(p: PersonaAcceso): string {
  // Los dueños los suma la configuración del servidor. Decía «la configuración (ACTIVA_DUENOS)»: el nombre de la
  // variable no le dice nada a quien mira la lista (QA del 08/10); el equipo técnico lo encuentra igual.
  const quien = p.agregadoPor === POR_CONFIGURACION ? "la configuración del sistema" : p.agregadoPor;
  return `Lo sumó ${quien}${elDia(p.agregadoEn)}`;
}

function leQuito(p: PersonaAcceso): string {
  return p.revocadoPor ? `Le quitó el acceso ${p.revocadoPor}${elDia(p.revocadoEn)}` : `Le quitaron el acceso${elDia(p.revocadoEn)}`;
}

const BOTON_FILA = "inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-600 disabled:opacity-40 transition-colors";

export default function AccesosPage() {
  const [datos, setDatos] = useState<ListaAccesos | null>(null);
  const [cargando, setCargando] = useState(true);
  const [falla, setFalla] = useState<"" | "error" | "sin-acceso" | "solo-equipo">("");
  const [textoFalla, setTextoFalla] = useState("");

  // alta
  const [email, setEmail] = useState("");
  const [nombre, setNombre] = useState("");
  const [rol, setRol] = useState<RolAcceso | "">("");
  const [comercios, setComercios] = useState<string[]>([]);
  const [errorRol, setErrorRol] = useState("");
  // los comercios para elegir (los de Activa y los del catálogo): se piden sólo si se puede sumar a alguien
  const [opciones, setOpciones] = useState<OpcionComercio[]>([]);
  // el nombre de cada comercio, para la lista (la ve todo el equipo, no sólo quien puede dar acceso)
  const [nombres, setNombres] = useState<Map<string, string>>(() => new Map());
  const [errorMail, setErrorMail] = useState("");
  const [errorAlta, setErrorAlta] = useState("");
  const [resultado, setResultado] = useState("");
  const [sumando, setSumando] = useState(false);

  // filas: una acción a la vez
  const [editando, setEditando] = useState<{ id: string; nombre: string; rol: RolAcceso; comercios: string[] } | null>(null);
  const [confirmando, setConfirmando] = useState("");
  const [desbloqueando, setDesbloqueando] = useState("");
  const [ocupada, setOcupada] = useState("");
  const [errorFila, setErrorFila] = useState<{ id: string; texto: string } | null>(null);

  const cargar = useCallback(async () => {
    try {
      setDatos(await accesosApi.listar());
      setFalla("");
    } catch (e) {
      // Sin acceso no hay nada que reintentar: se cierra la sesión.
      if (esSinAcceso(e)) {
        setFalla("sin-acceso");
        avisarSesionVencida();
      } else if (esSoloEquipo(e)) {
        setFalla("solo-equipo");
        setTextoFalla((e as Error)?.message || "");
      } else if (esSinPermiso(e)) {
        setFalla("solo-equipo");
        setTextoFalla(TEXTO_SIN_PERMISO);
      } else {
        setFalla("error");
      }
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { void cargar(); }, [cargar]);

  // Los comercios: para elegir (quien puede dar acceso) y para mostrar su nombre en la lista (todo el equipo que la
  // ve). Si no se pueden traer, igual se puede escribir el identificador a mano y la lista muestra los identificadores.
  const veLaLista = !!datos;
  useEffect(() => {
    if (!veLaLista) return;
    let vivo = true;
    accesosApi.marcas().then((l) => {
      if (!vivo) return;
      setOpciones(opcionesDeComercios(l));
      setNombres(nombresDeComercios(l));
    }, () => undefined);
    return () => { vivo = false; };
  }, [veLaLista]);

  const reintentar = () => {
    setCargando(true);
    void cargar();
  };

  /** El texto del resultado de un alta o de devolver el acceso: si no le llegó el mail, el link. */
  const textoListo = (mail: string, avisoEnviado: boolean) =>
    avisoEnviado
      ? `Listo: ${mail} ya puede entrar. Le avisamos por mail.`
      : `Listo: ${mail} ya puede entrar. No pudimos avisarle por mail: pasale vos el link ${datos?.urlLogin || ""}.`;

  const sumar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sumando) return;
    const limpio = email.trim().toLowerCase();
    const problema = problemaDelMail(limpio);
    const problemaRol = !rol ? FALTA_ROL : rol === "comercio" && (comercios.length < 1 || comercios.length > MAX_COMERCIOS) ? FALTAN_COMERCIOS : "";
    setErrorMail(problema);
    setErrorRol(problemaRol);
    setErrorAlta("");
    setResultado("");
    if (problema || problemaRol || !rol) return;
    setSumando(true);
    try {
      const nombreLimpio = nombre.trim();
      const r = await accesosApi.agregar({
        email: limpio,
        ...(nombreLimpio ? { nombre: nombreLimpio } : {}),
        rol,
        ...(rol === "comercio" ? { comercios } : {}),
      });
      // se limpia SÓLO si salió bien: si no, se corrige lo tipeado en vez de reescribirlo
      setEmail("");
      setNombre("");
      setRol("");
      setComercios([]);
      setResultado(textoListo(r?.persona?.email || limpio, !!r?.avisoEnviado) + avisoSinVerificar(r?.comerciosSinVerificar));
      await cargar();
    } catch (err) {
      setErrorAlta((err as Error)?.message || "No se pudo dar el acceso.");
    } finally {
      setSumando(false);
    }
  };

  /** Una acción sobre una fila: una a la vez, con su error en la fila, y la lista se recarga si salió. */
  const enFila = async (id: string, accion: () => Promise<unknown>) => {
    if (ocupada) return;
    setOcupada(id);
    setErrorFila(null);
    try {
      await accion();
      setConfirmando("");
      setDesbloqueando("");
      setEditando(null);
      await cargar();
    } catch (err) {
      setErrorFila({ id, texto: (err as Error)?.message || "No se pudo completar." });
      if (esSinAcceso(err)) avisarSesionVencida();
    } finally {
      setOcupada("");
    }
  };

  const quitar = (p: PersonaAcceso) => enFila(p.id, () => accesosApi.revocar(p.id));

  const devolver = (p: PersonaAcceso) =>
    enFila(p.id, async () => {
      const r = await accesosApi.reactivar(p.id);
      setResultado(textoListo(p.email, !!r?.avisoEnviado));
    });

  const desbloquear = (p: PersonaAcceso) =>
    enFila(p.id, async () => {
      await accesosApi.desbloquear(p.id);
      setResultado(`Listo: ${p.email} ya puede pedir un código nuevo.`);
    });

  /** Manda sólo lo que cambió: el nombre, o el rol con sus comercios. Sin cambios, cierra. */
  const guardarEdicion = (p: PersonaAcceso) => {
    if (!editando) return;
    const nuevo = editando.nombre.trim();
    if (!nuevo) return;
    if (editando.rol === "comercio" && (editando.comercios.length < 1 || editando.comercios.length > MAX_COMERCIOS)) {
      setErrorFila({ id: p.id, texto: FALTAN_COMERCIOS });
      return;
    }
    const antes = [...(p.comercios || [])].sort().join(",");
    const cambiaRol = editando.rol !== rolDe(p) || (editando.rol === "comercio" && [...editando.comercios].sort().join(",") !== antes);
    const cambios = {
      ...(nuevo !== p.nombre ? { nombre: nuevo } : {}),
      ...(cambiaRol ? { rol: editando.rol, ...(editando.rol === "comercio" ? { comercios: editando.comercios } : {}) } : {}),
    };
    if (Object.keys(cambios).length === 0) {
      setEditando(null);
      return;
    }
    void enFila(p.id, async () => {
      const r = await accesosApi.editar(p.id, cambios);
      const aviso = avisoSinVerificar(r?.comerciosSinVerificar);
      if (aviso) setResultado(`Listo: se guardó.${aviso}`);
    });
  };

  const abrirEdicion = (p: PersonaAcceso) => {
    setConfirmando("");
    setDesbloqueando("");
    setErrorFila(null);
    setEditando({ id: p.id, nombre: p.nombre, rol: rolDe(p), comercios: [...(p.comercios || [])] });
  };

  const abrirConfirmacion = (p: PersonaAcceso) => {
    setEditando(null);
    setDesbloqueando("");
    setErrorFila(null);
    setConfirmando(p.id);
  };

  const abrirDesbloqueo = (p: PersonaAcceso) => {
    setEditando(null);
    setConfirmando("");
    setErrorFila(null);
    setDesbloqueando(p.id);
  };

  const encabezado = (
    <EncabezadoPagina
      antetitulo="Accesos"
      titulo="Quién puede entrar"
      bajada="Sumá por mail a las personas que pueden entrar a DEENEX Activa, de Deenex o de afuera. Cada vez que entran, les llega un código a ese mail: no hay contraseñas."
    />
  );

  let cuerpo: React.ReactNode;
  if (cargando) {
    cuerpo = (
      <div className="flex items-center justify-center gap-2 py-12 text-slate-400 text-sm">
        <Loader2 size={16} className="animate-spin" /> Cargando las personas con acceso…
      </div>
    );
  } else if (falla === "sin-acceso") {
    cuerpo = <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 text-center text-sm text-slate-600">Ya no tenés acceso.</div>;
  } else if (falla === "solo-equipo") {
    cuerpo = (
      <div className="rounded-2xl border border-slate-100 bg-white shadow-sm p-6 text-center text-sm text-slate-600">
        {textoFalla || "La lista de Accesos la ve el equipo de Deenex."}
      </div>
    );
  } else if (!datos) {
    cuerpo = <CajaError titulo="No pudimos cargar la lista." onReintentar={reintentar} />;
  } else {
    const { soyDueno, claveCompartidaActiva } = datos;
    const activos = datos.personas.filter((p) => p.estado === "activo");
    const revocados = datos.personas.filter((p) => p.estado !== "activo");
    const soloVos = activos.length === 1 && activos[0].esVos;

    const filaActiva = (p: PersonaAcceso) => {
      const editandoEsta = editando?.id === p.id;
      const confirmandoEsta = confirmando === p.id;
      const desbloqueandoEsta = desbloqueando === p.id;
      return (
        <li key={p.id} className="px-4 py-3">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              {editandoEsta ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      value={editando.nombre}
                      onChange={(e) => setEditando({ ...editando, nombre: e.target.value })}
                      onKeyDown={(e) => e.key === "Enter" && guardarEdicion(p)}
                      aria-label={`Nombre de ${p.nombre}`}
                      maxLength={80}
                      autoFocus
                      className="flex-1 min-w-[160px] rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                    />
                    <button type="button" onClick={() => guardarEdicion(p)} disabled={!editando.nombre.trim() || ocupada === p.id} className={BOTON_FILA}>
                      {ocupada === p.id && <Loader2 size={12} className="animate-spin" />} Guardar
                    </button>
                    <button type="button" onClick={() => setEditando(null)} className={BOTON_FILA}>Cancelar</button>
                  </div>
                  <ElegirRol
                    nombre={`rol-${p.id}`}
                    valor={editando.rol}
                    onCambio={(r) => setEditando({ ...editando, rol: r })}
                    sinCliente={p.esDueno ? "Es dueño de Accesos: un dueño es siempre del equipo." : undefined}
                  />
                  {editando.rol === "comercio" && (
                    <SelectorComercios opciones={opciones} elegidos={editando.comercios} onCambio={(c) => setEditando({ ...editando, comercios: c })} />
                  )}
                </div>
              ) : (
                <p className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-slate-800">
                  <span className="truncate">{p.nombre}</span>
                  {p.esDueno && <span className="text-[9px] font-bold uppercase tracking-wider text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded">Dueño</span>}
                  {p.esVos && <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">Sos vos</span>}
                </p>
              )}
              <p className="text-xs text-slate-500 truncate">{p.email}</p>
              <p className={`text-[11px] ${rolDe(p) === "comercio" ? "font-semibold text-violet-700" : "text-slate-500"}`}>{aQueEntra(p, nombres)}</p>
              <p className="text-[11px] text-slate-400">{loSumo(p)}</p>
              <p className="text-[11px] text-slate-400">{p.ultimoIngresoEn ? `Último ingreso: ${fmtFecha(p.ultimoIngresoEn)}` : "Todavía no entró"}</p>
            </div>
            {soyDueno && !editandoEsta && !confirmandoEsta && !desbloqueandoEsta && (
              <div className="flex flex-shrink-0 flex-wrap justify-end gap-1.5">
                <button type="button" onClick={() => abrirEdicion(p)} disabled={!!ocupada} className={BOTON_FILA}>
                  <Pencil size={12} /> Editar
                </button>
                {/* Para quien no puede entrar desde un navegador nuevo (un tercero le gastó los códigos equivocados o los pedidos del día) o se frenó el suyo. También a un dueño. */}
                <button type="button" onClick={() => abrirDesbloqueo(p)} disabled={!!ocupada} className={BOTON_FILA}>
                  <LockOpen size={12} /> Desbloquear el ingreso
                </button>
                {/* A un dueño, recién cuando lo sacan de ACTIVA_DUENOS; a uno mismo, nunca desde acá. */}
                {!p.esDueno && !p.esVos && (
                  <button type="button" onClick={() => abrirConfirmacion(p)} disabled={!!ocupada} className={`${BOTON_FILA} hover:border-red-300 hover:text-red-600`}>
                    <UserMinus size={12} /> Quitar acceso
                  </button>
                )}
              </div>
            )}
          </div>

          {confirmandoEsta && (
            <div className="mt-3 rounded-xl border border-red-100 bg-red-50/60 p-3 space-y-2">
              <p className="text-sm text-red-800">
                {`¿Le quitás el acceso a ${p.nombre}? Deja de poder entrar en menos de un minuto y se le cierran las sesiones abiertas.`}
              </p>
              {claveCompartidaActiva && (
                <p className="text-xs font-semibold text-red-700">Ojo: mientras exista la clave del equipo, si la conoce puede seguir entrando con ella.</p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void quitar(p)}
                  disabled={ocupada === p.id}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-40 transition-colors"
                >
                  {ocupada === p.id && <Loader2 size={12} className="animate-spin" />} Sí, quitar
                </button>
                <button type="button" onClick={() => setConfirmando("")} disabled={ocupada === p.id} className={BOTON_FILA}>Cancelar</button>
              </div>
            </div>
          )}

          {desbloqueandoEsta && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2">
              <p className="text-sm text-slate-700">
                {`¿Desbloqueás el ingreso de ${p.nombre}? Se borran los códigos equivocados y los códigos pedidos de sus últimas 24 horas: puede volver a pedir uno desde cualquier navegador. Si ${p.nombre} no pidió esos códigos, alguien estuvo probando con su mail, y desbloquear también le devuelve intentos: hacelo cuando ${p.nombre} esté por entrar.`}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void desbloquear(p)}
                  disabled={ocupada === p.id}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-40 transition-colors"
                >
                  {ocupada === p.id && <Loader2 size={12} className="animate-spin" />} Sí, desbloquear
                </button>
                <button type="button" onClick={() => setDesbloqueando("")} disabled={ocupada === p.id} className={BOTON_FILA}>Cancelar</button>
              </div>
            </div>
          )}

          {errorFila?.id === p.id && <p className="mt-2 text-xs text-red-500">{errorFila.texto}</p>}
        </li>
      );
    };

    const filaRevocada = (p: PersonaAcceso) => (
      <li key={p.id} className="px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-600 truncate">{p.nombre}</p>
            <p className="text-xs text-slate-500 truncate">{p.email}</p>
            <p className="text-[11px] text-slate-400">{aQueEntra(p, nombres)}</p>
            <p className="text-[11px] text-slate-400">{leQuito(p)}</p>
          </div>
          {soyDueno && (
            <button type="button" onClick={() => void devolver(p)} disabled={!!ocupada} className={`${BOTON_FILA} flex-shrink-0`}>
              {ocupada === p.id ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />} Volver a dar acceso
            </button>
          )}
        </div>
        {errorFila?.id === p.id && <p className="mt-2 text-xs text-red-500">{errorFila.texto}</p>}
      </li>
    );

    cuerpo = (
      <>
        {!datos.smtpConfigurado && (
          <AvisoAmbar>Todavía no está configurado el envío de mails: las personas que sumes no van a poder entrar hasta que se configure el servidor de mail.</AvisoAmbar>
        )}
        {claveCompartidaActiva && (
          <AvisoAmbar>La clave del equipo sigue activa: quien la conozca puede entrar a todos los comercios aunque no esté en esta lista. Pedile a Alan que la retire.</AvisoAmbar>
        )}

        {soyDueno ? (
          <form onSubmit={sumar} noValidate className="rounded-2xl border border-slate-100 bg-white shadow-sm p-4 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Sumar a alguien</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="acceso-mail" className="text-xs font-semibold text-slate-600">Mail</label>
                <input
                  id="acceso-mail"
                  type="email"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setErrorMail(""); }}
                  placeholder="nombre@empresa.com"
                  aria-invalid={!!errorMail}
                  aria-describedby={errorMail ? "acceso-mail-error" : undefined}
                  className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200 aria-[invalid=true]:border-red-300"
                />
                {errorMail && <p id="acceso-mail-error" className="text-[11px] text-red-600 mt-1">{errorMail}</p>}
                {!errorMail && rol !== "comercio" && MAIL_RE.test(email.trim()) && !email.trim().toLowerCase().endsWith(DOMINIO_EQUIPO) && (
                  <p className="text-[11px] font-semibold text-amber-700 mt-1">{AVISO_MAIL_DE_AFUERA}</p>
                )}
              </div>
              <div>
                <label htmlFor="acceso-nombre" className="text-xs font-semibold text-slate-600">Nombre (opcional)</label>
                <input
                  id="acceso-nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  maxLength={80}
                  placeholder="Cómo aparece en la lista y en lo que hace"
                  aria-describedby="acceso-nombre-ayuda"
                  className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
                />
                <p id="acceso-nombre-ayuda" className="text-[11px] text-slate-400 mt-1">Si lo dejás vacío, usamos lo que está antes de la @.</p>
              </div>
            </div>
            <ElegirRol nombre="rol-alta" valor={rol} onCambio={(r) => { setRol(r); setErrorRol(""); }} />
            {rol === "comercio" && (
              <SelectorComercios opciones={opciones} elegidos={comercios} onCambio={(c) => { setComercios(c); setErrorRol(""); }} />
            )}
            {errorRol && <p className="text-[11px] text-red-600">{errorRol}</p>}
            <button
              type="submit"
              disabled={sumando || !email.trim()}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-slate-900 text-white px-4 py-2 text-sm font-semibold disabled:opacity-40 hover:bg-slate-800 transition-colors"
            >
              {sumando ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} {sumando ? "Sumando…" : "Dar acceso"}
            </button>
            <p className="text-[11px] text-slate-400">
              {rol ? `${AYUDA_ROL[rol]} ${AYUDA_DESBLOQUEO}` : `${AYUDA_ROL.equipo} ${AYUDA_ROL.comercio} ${AYUDA_DESBLOQUEO}`}
            </p>
            {errorAlta && <p className="text-xs text-red-500">{errorAlta}</p>}
            {resultado && <p role="status" className="text-xs font-medium text-emerald-700">{resultado}</p>}
          </form>
        ) : (
          <p className="rounded-2xl border border-slate-100 bg-white shadow-sm p-4 text-sm text-slate-600">
            {datos.duenos.length > 0
              ? `Sólo los dueños de Accesos pueden sumar o quitar personas: ${enumerar(datos.duenos.map((d) => d.nombre))}.`
              : "Sólo los dueños de Accesos pueden sumar o quitar personas."}
          </p>
        )}

        {falla === "error" && (
          <p className="text-xs text-red-500">
            No pudimos actualizar la lista.{" "}
            <button type="button" onClick={reintentar} className="font-semibold underline">Reintentar</button>
          </p>
        )}

        <div className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
            <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500">{`Con acceso (${activos.length})`}</h2>
          </div>
          <ul className="divide-y divide-slate-50">{activos.map(filaActiva)}</ul>
          {soloVos && (
            <p className="px-4 py-6 text-center text-sm text-slate-400">
              {soyDueno ? "Por ahora entrás sólo vos. Sumá a alguien con el formulario de arriba." : "Por ahora entrás sólo vos."}
            </p>
          )}
        </div>

        {revocados.length > 0 && (
          <details className="rounded-2xl border border-slate-100 bg-white shadow-sm overflow-hidden">
            <summary className="px-4 py-2.5 bg-slate-50 cursor-pointer text-xs font-bold uppercase tracking-wider text-slate-500">
              {`Sin acceso (${revocados.length})`}
            </summary>
            <ul className="divide-y divide-slate-50 border-t border-slate-100">{revocados.map(filaRevocada)}</ul>
          </details>
        )}
      </>
    );
  }

  return (
    <Pagina angosta>
      {encabezado}
      {cuerpo}
    </Pagina>
  );
}
